import { useEffect, useMemo, useRef, useState } from 'react';
import SeletorQuantidade from './SeletorQuantidade';
import FundoRifa from './FundoRifa';
import { validar, irParaOPrimeiro, type CampoGuiado } from '@/lib/formularioGuiado';
import type { TalaoPublico } from '../types';
import {
  calcularContaOferta,
  formatarCentavos,
  formatarReais,
  fraseSorteio,
} from '../utils';

interface Props {
  talao: TalaoPublico;
  quantidade: number;
  aoMudarQuantidade: (v: number) => void;
  nome: string;
  aoMudarNome: (v: string) => void;
  telefone: string;
  aoMudarTelefone: (v: string) => void;
  erro: string | null;
  gerando: boolean;
  aoPagar: () => void;
  imagemFundo?: string | null;
}

function iniciaisNome(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  if (partes.length === 0 || !partes[0]) return '?';
  if (partes.length === 1) return partes[0].charAt(0).toUpperCase();
  return (partes[0].charAt(0) + partes[partes.length - 1].charAt(0)).toUpperCase();
}

export default function PassoOferta({
  talao,
  quantidade,
  aoMudarQuantidade,
  nome,
  aoMudarNome,
  telefone,
  aoMudarTelefone,
  erro,
  gerando,
  aoPagar,
  imagemFundo,
}: Props) {
  const [mostrarRegulamento, setMostrarRegulamento] = useState(false);
  const [carrosselIdx, setCarrosselIdx] = useState(0);
  const [totalPulsando, setTotalPulsando] = useState(false);
  const [montado, setMontado] = useState(false);
  const [erros, setErros] = useState<{ nome?: string; whatsapp?: string }>({});
  const quantidadeAnterior = useRef(quantidade);

  useEffect(() => {
    const t = setTimeout(() => setMontado(true), 30);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (quantidadeAnterior.current !== quantidade) {
      quantidadeAnterior.current = quantidade;
      setTotalPulsando(true);
      const t = setTimeout(() => setTotalPulsando(false), 180);
      return () => clearTimeout(t);
    }
  }, [quantidade]);

  const limite = Math.max(1, Math.min(talao.max_por_compra, talao.restam));
  const conta = useMemo(
    () => calcularContaOferta(talao.valor_cota, quantidade, talao.taxa_por_dentro),
    [talao.valor_cota, quantidade, talao.taxa_por_dentro],
  );

  const premios = talao.premios ?? [];
  const frase = fraseSorteio(talao);
  const totalTexto = formatarCentavos(conta.totalCentavos);
  const ultimosNumeros = talao.restam <= 20;

  const cardClasses =
    'bg-white/85 backdrop-blur-md rounded-3xl shadow-xl p-5 md:p-6';

  function handleCarrosselAnterior() {
    setCarrosselIdx((i) => Math.max(0, i - 1));
  }
  function handleCarrosselProximo() {
    setCarrosselIdx((i) => Math.min(premios.length - 1, i + 1));
  }

  function validarWhatsappNoBlur() {
    const d = telefone.replace(/\D/g, '');
    if (d.length > 0 && d.length < 10) {
      setErros((e) => ({ ...e, whatsapp: 'Informe seu WhatsApp para receber seus números.' }));
    }
  }

  function pagar() {
    const errosNovos = {
      nome: nome.trim() ? undefined : 'Falta seu nome.',
      whatsapp:
        telefone.replace(/\D/g, '').length >= 10
          ? undefined
          : 'Informe seu WhatsApp para receber seus números.',
    };
    setErros(errosNovos);

    const campos: CampoGuiado[] = [
      { ref: document.getElementById('talao-nome'), rotulo: 'Seu nome', mensagem: errosNovos.nome ?? null },
      { ref: document.getElementById('talao-whats'), rotulo: 'Seu WhatsApp', mensagem: errosNovos.whatsapp ?? null },
    ];
    const invalidos = validar(campos);
    if (invalidos.length > 0) {
      irParaOPrimeiro(invalidos);
      return;
    }
    aoPagar();
  }

  return (
    <FundoRifa imagemUrl={imagemFundo}>
      <div
        className="mx-auto w-full max-w-[560px] px-4 py-6 pb-32 transition-all duration-[220ms] ease-out md:pb-6"
        style={{
          opacity: montado ? 1 : 0,
          transform: montado ? 'translateY(0)' : 'translateY(12px)',
        }}
      >
        {/* ── Cabeçalho sobre fundo ── */}
        <div className="mb-5 text-center">
          <h1 className="text-3xl font-bold leading-tight text-white drop-shadow-lg">
            {talao.titulo}
          </h1>
          {talao.entidade && (
            <p className="mt-1 text-base text-white/90 drop-shadow">
              Você está ajudando <strong className="font-semibold">{talao.entidade}</strong>
            </p>
          )}

          {/* Pill do vendedor */}
          <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1.5 backdrop-blur-sm">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-white">
              {iniciaisNome(talao.vendedor)}
            </span>
            <span className="text-sm font-medium text-white">
              Comprando com <strong>{talao.vendedor}</strong>
            </span>
          </div>

          {/* Pill de restam */}
          <div className="mt-2 flex justify-center">
            <span
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${
                ultimosNumeros
                  ? 'bg-amber-400/90 text-amber-900'
                  : 'bg-white/20 text-white'
              }`}
            >
              <i className="ri-ticket-2-line" />
              {ultimosNumeros
                ? `Últimos ${talao.restam}!`
                : `Restam ${talao.restam} números`}
            </span>
          </div>
        </div>

        {/* ── Cartão: Prêmios ── */}
        {premios.length > 0 && (
          <div className={`${cardClasses} mb-3`}>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-secondary-500">
              Prêmios
            </p>

            {premios.length === 1 ? (
              <div>
                {premios[0].imagem_url ? (
                  <img
                    src={premios[0].imagem_url}
                    alt={premios[0].descricao}
                    className="h-44 w-full rounded-2xl object-cover object-top"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="flex h-36 w-full items-center justify-center rounded-2xl bg-primary-100">
                    <i className="ri-gift-2-line text-5xl text-primary-400" />
                  </div>
                )}
                <p className="mt-2 text-base font-semibold text-foreground-950">
                  {premios[0].descricao}
                </p>
                {premios[0].doado_por && (
                  <p className="mt-0.5 text-xs text-secondary-500">
                    doado por {premios[0].doado_por}
                  </p>
                )}
              </div>
            ) : (
              /* Carrossel */
              <div className="relative">
                <div className="overflow-hidden">
                  <div
                    className="flex transition-transform duration-300 ease-out"
                    style={{ transform: `translateX(-${carrosselIdx * 100}%)` }}
                  >
                    {premios.map((p) => (
                      <div key={p.ordem} className="min-w-full">
                        {p.imagem_url ? (
                          <img
                            src={p.imagem_url}
                            alt={p.descricao}
                            className="h-44 w-full rounded-2xl object-cover object-top"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <div className="flex h-36 w-full items-center justify-center rounded-2xl bg-primary-100">
                            <i className="ri-gift-2-line text-5xl text-primary-400" />
                          </div>
                        )}
                        <p className="mt-2 text-base font-semibold text-foreground-950">
                          {p.ordem}º {p.descricao}
                        </p>
                        {p.doado_por && (
                          <p className="mt-0.5 text-xs text-secondary-500">
                            doado por {p.doado_por}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {carrosselIdx > 0 && (
                  <button
                    type="button"
                    onClick={handleCarrosselAnterior}
                    aria-label="Prêmio anterior"
                    className="absolute left-0 top-14 flex h-8 w-8 -translate-x-3 cursor-pointer items-center justify-center rounded-full bg-white/80 shadow backdrop-blur-sm"
                  >
                    <i className="ri-arrow-left-s-line text-lg text-foreground-700" />
                  </button>
                )}
                {carrosselIdx < premios.length - 1 && (
                  <button
                    type="button"
                    onClick={handleCarrosselProximo}
                    aria-label="Próximo prêmio"
                    className="absolute right-0 top-14 flex h-8 w-8 translate-x-3 cursor-pointer items-center justify-center rounded-full bg-white/80 shadow backdrop-blur-sm"
                  >
                    <i className="ri-arrow-right-s-line text-lg text-foreground-700" />
                  </button>
                )}

                {/* dots */}
                <div className="mt-3 flex justify-center gap-1.5">
                  {premios.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setCarrosselIdx(i)}
                      aria-label={`Prêmio ${i + 1}`}
                      className={`h-2 rounded-full transition-all cursor-pointer ${
                        i === carrosselIdx ? 'w-5 bg-primary-500' : 'w-2 bg-secondary-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}

            <p className="mt-2 text-xs text-secondary-500">{frase}</p>
          </div>
        )}

        {/* ── Cartão: Quantidade ── */}
        <div className={`${cardClasses} mb-3`}>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-secondary-500">
            Quantos números você quer?
          </p>
          <div className="flex items-center justify-between">
            <span className="text-sm text-secondary-600">
              {formatarReais(talao.valor_cota)} cada
            </span>
            <span
              className="text-lg font-bold text-foreground-950 transition-transform duration-[180ms]"
              style={{
                transform: totalPulsando ? 'scale(1.06)' : 'scale(1)',
              }}
            >
              Total: {totalTexto}
            </span>
          </div>
          <div className="mt-4">
            <SeletorQuantidade
              quantidade={quantidade}
              maximo={limite}
              aoMudar={aoMudarQuantidade}
            />
          </div>
          {conta.mostrarTaxa && (
            <p className="mt-3 text-center text-xs text-secondary-400">
              Inclui taxa de operação de {formatarCentavos(conta.taxaCentavos)}
            </p>
          )}
        </div>

        {/* ── Cartão: Dados ── */}
        <div className={`${cardClasses} mb-3`}>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-secondary-500">
            Seus dados
          </p>

          {/* Campo nome */}
          <div className="relative mb-3">
            <label
              htmlFor="talao-nome"
              className="absolute -top-2 left-3 bg-white/90 px-1 text-xs font-medium text-secondary-600"
            >
              Seu nome *
            </label>
            <div className={`flex items-center overflow-hidden rounded-2xl border bg-white/80 ${erros.nome ? 'border-danger-500' : 'border-secondary-300'}`}>
              <span className="flex h-12 w-12 items-center justify-center text-secondary-400">
                <i className="ri-user-line text-lg" />
              </span>
              <input
                id="talao-nome"
                type="text"
                value={nome}
                onChange={(e) => {
                  aoMudarNome(e.target.value);
                  setErros((er) => ({ ...er, nome: undefined }));
                }}
                placeholder="Maria da Silva"
                className="flex-1 bg-transparent py-3 pr-4 text-sm text-foreground-900 outline-none"
              />
            </div>
            {erros.nome && (
              <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
                {erros.nome}
              </p>
            )}
          </div>

          {/* Campo telefone */}
          <div className="relative">
            <label
              htmlFor="talao-whats"
              className="absolute -top-2 left-3 bg-white/90 px-1 text-xs font-medium text-secondary-600"
            >
              Seu WhatsApp *
            </label>
            <div className={`flex items-center overflow-hidden rounded-2xl border bg-white/80 ${erros.whatsapp ? 'border-danger-500' : 'border-secondary-300'}`}>
              <span className="flex h-12 w-12 items-center justify-center text-secondary-400">
                <i className="ri-whatsapp-line text-lg" />
              </span>
              <input
                id="talao-whats"
                type="tel"
                value={telefone}
                onChange={(e) => {
                  aoMudarTelefone(e.target.value);
                  setErros((er) => ({ ...er, whatsapp: undefined }));
                }}
                onBlur={validarWhatsappNoBlur}
                placeholder="(00) 00000-0000"
                inputMode="numeric"
                className="flex-1 bg-transparent py-3 pr-4 text-sm text-foreground-900 outline-none"
              />
            </div>
            {erros.whatsapp && (
              <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
                {erros.whatsapp}
              </p>
            )}
          </div>

          <p className="mt-3 text-center text-xs text-secondary-500">
            Seus números aparecem aqui na hora e você recebe um comprovante.
          </p>
        </div>

        {erro && (
          <div className="mb-3 rounded-2xl border border-red-200 bg-red-50/90 px-4 py-3 text-sm text-red-700 backdrop-blur-sm">
            {erro}
          </div>
        )}

        {talao.regulamento && (
          <div className="mb-4 text-center">
            <button
              type="button"
              onClick={() => setMostrarRegulamento(true)}
              className="cursor-pointer text-xs text-white/80 underline"
            >
              Ver regulamento
            </button>
          </div>
        )}
      </div>

      {/* ── Botão sticky no celular / normal no desktop ── */}
      <div className="fixed bottom-0 left-0 right-0 z-20 px-4 pb-4 md:relative md:bottom-auto md:mx-auto md:max-w-[560px] md:px-4 md:pb-8">
        <div className="md:hidden h-3" />
        <button
          type="button"
          onClick={pagar}
          disabled={gerando}
          className="w-full cursor-pointer whitespace-nowrap rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4 text-lg font-bold text-white shadow-lg shadow-primary-500/30 transition-all hover:from-primary-600 hover:to-primary-700 disabled:cursor-not-allowed disabled:from-secondary-300 disabled:to-secondary-400 disabled:shadow-none"
        >
          {gerando ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin" />
              Gerando…
            </span>
          ) : (
            <span
              className="transition-transform duration-[180ms]"
              style={{ display: 'inline-block' }}
            >
              Pagar {totalTexto} no Pix
            </span>
          )}
        </button>
      </div>

      {/* ── Modal regulamento ── */}
      {mostrarRegulamento && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
          <div className="max-h-[85vh] w-full max-w-[560px] overflow-hidden rounded-t-3xl bg-white sm:rounded-3xl">
            <div className="flex items-center justify-between border-b border-secondary-200 px-5 py-4">
              <h3 className="text-base font-semibold text-foreground-950">Regulamento</h3>
              <button
                type="button"
                onClick={() => setMostrarRegulamento(false)}
                aria-label="Fechar"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-secondary-600 transition-colors hover:bg-background-100"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto whitespace-pre-wrap px-5 py-4 text-sm text-foreground-800">
              {talao.regulamento}
            </div>
          </div>
        </div>
      )}
    </FundoRifa>
  );
}